/**
 * ReglagesRhContext — le réglage RH COMMUN (suivi RH, lot 5, 27/10/2026) : listes de choix
 * modifiables par la RH et couleurs du thème (D-21 A : réglage commun, stocké sur le serveur,
 * journalisé ; modifiable par le fondateur et le responsable d'exploitation, lu par le chef
 * d'équipe).
 *
 * - UN GET /api/suivi-rh/reglages au montage (« lu une fois au chargement ») ; relu sur
 *   l'événement temps réel « contracts » d'action « reglages_rh » et après chaque écriture
 *   faite depuis cet écran (l'événement n'est pas renvoyé à son auteur).
 * - Pose sur document.documentElement UNIQUEMENT des variables --rh-* (--rh-or, les 7 statuts
 *   de départ, --rh-statut-<clé> des ajouts). index.css garde les valeurs de départ : si le
 *   serveur ne répond pas, elles restent, et les blocs le disent (règle 6.19).
 * - Ne touche jamais aux préférences PAR UTILISATEUR (PrefsContext : --accent, police…).
 * - Sans fournisseur (tests jest), useReglagesRh() rend les valeurs de départ, sans appel réseau.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL as API } from '../lib/api';
import { useSSE } from '../lib/sse';
import { fusionnerReglages, variablesCss, REGLAGES_DEPART } from '../lib/reglagesRh';

const ReglagesRhContext = createContext(null);
const SSE_TYPES = ['contracts'];
const opts = { withCredentials: true };

const SANS_FOURNISSEUR = Object.freeze({
  reglages: REGLAGES_DEPART,
  erreur: '',
  charge: false,
  avecFournisseur: false,
  relire: async () => REGLAGES_DEPART,
});

export function useReglagesRh() {
  return useContext(ReglagesRhContext) || SANS_FOURNISSEUR;
}

/** Pose les variables --rh-* et retire celles posées la fois d'avant qui ont disparu. */
function poserVariables(vars, precedentes, racine) {
  if (!racine || !racine.style) return;
  for (const k of precedentes) if (!(k in vars)) racine.style.removeProperty(k);
  for (const [k, v] of Object.entries(vars)) racine.style.setProperty(k, v);
}

export function ReglagesRhProvider({ children }) {
  const [reponse, setReponse] = useState(null);
  const [erreur, setErreur] = useState('');
  const [charge, setCharge] = useState(false);
  const posees = useRef([]);
  const sequence = useRef(0);

  const relire = useCallback(async () => {
    const seq = ++sequence.current;
    try {
      const { data } = await axios.get(`${API}/api/suivi-rh/reglages`, opts);
      if (seq !== sequence.current) return null;
      setReponse(data && typeof data === 'object' ? data : null);
      setErreur(data && data.listes ? '' : 'réponse du serveur illisible');
      return data;
    } catch (e) {
      if (seq !== sequence.current) return null;
      const st = e && e.response && e.response.status;
      setErreur(st ? `réglage non lu (erreur ${st})` : 'réglage non lu (serveur injoignable)');
      return null;
    } finally {
      if (seq === sequence.current) setCharge(true);
    }
  }, []);

  useEffect(() => { relire(); }, [relire]);

  const onSse = useCallback((ev) => {
    if (ev && (ev.action === 'reglages_rh' || ev.type === 'resync')) relire();
  }, [relire]);
  useSSE(onSse, SSE_TYPES);

  const reglages = useMemo(() => fusionnerReglages(reponse), [reponse]);

  // Les variables ne sont posées qu'une fois le réglage du serveur lu (sinon index.css fait foi).
  useEffect(() => {
    if (reglages.depart) return;
    const vars = variablesCss(reglages);
    const racine = typeof document !== 'undefined' ? document.documentElement : null;
    poserVariables(vars, posees.current, racine);
    posees.current = Object.keys(vars);
  }, [reglages]);

  // Au démontage, la page revient aux valeurs de index.css.
  useEffect(() => () => {
    const racine = typeof document !== 'undefined' ? document.documentElement : null;
    if (racine && racine.style) for (const k of posees.current) racine.style.removeProperty(k);
    posees.current = [];
  }, []);

  const valeur = useMemo(() => ({ reglages, erreur, charge, avecFournisseur: true, relire }),
    [reglages, erreur, charge, relire]);

  return <ReglagesRhContext.Provider value={valeur}>{children}</ReglagesRhContext.Provider>;
}
